"use client";
import { getApiToken as getApiTokenAsync, clearApiToken } from "@/lib/token";
import { getBrowserBase, getServerBase, join } from "@/lib/apiBase";

// Prefer routing via the Next.js same-origin proxy when a public API base
// is not explicitly configured. This avoids relying on external Traefik
// path rules (e.g., beta host) and keeps CORS simple.
// Proxy is opt-in only. Default to direct calls to '/api/*' on the same origin
// (Traefik or the hosting layer should route '/api/*' to the backend).
const USE_PROXY: boolean = (() => {
  const explicit = (process.env.NEXT_PUBLIC_USE_PROXY || '').toLowerCase() === 'true';
  return explicit;
})();

// Paths handled by Next.js route handlers — must stay same-origin (never rewritten to external API base).
// These have route.ts files under src/app/api/ and run server-side, so they bypass CORS entirely.
const NEXTJS_HANDLED_PREFIXES = [
  '/api/auth/',
  '/api/profile/',
  '/api/account/',
  '/api/admin/',
  '/api/age-verify',
  '/api/analytics',
  '/api/categories',
  '/api/content-words',
  '/api/dev/',
  '/api/engagement/',
  '/api/feed',
  '/api/filters/',
  '/api/front-page',
  '/api/frontpage',
  '/api/FrontPageData',
  '/api/gallery',
  '/api/health',
  '/api/plans',
  '/api/post',
  '/api/posts/',
  '/api/proxy/',
  '/api/resolve/',
  '/api/revalidate',
  '/api/search2',
  '/api/twitter/',
  '/api/video/',
  '/api/vitals',
];

function isNextjsHandled(p: string): boolean {
  return NEXTJS_HANDLED_PREFIXES.some(prefix =>
    p === prefix.replace(/\/$/, '') || p.startsWith(prefix) || p.startsWith(prefix.replace(/\/$/, '') + '/')
  );
}

export function buildClientUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (USE_PROXY) {
    if (p.startsWith('/api/proxy/')) return p; // already proxied
    if (p.startsWith('/api/')) return `/api/proxy${p}`; // route API calls through same-origin proxy
  }
  // Paths served by Next.js route handlers must stay same-origin — they handle auth server-side
  // and forwarding them to the external API base would trigger CORS.
  if (isNextjsHandled(p)) return p;
  // Not using proxy: ensure API paths go to the configured public API base
  if (p.startsWith('/api/')) {
    const base = getBrowserBase();
    return base ? join(base, p) : p;
  }
  return join(getBrowserBase(), p);
}

function extractTokenFromSession(session: any): string | undefined {
  try {
    return (
      session?.accessToken ||
      session?.user?.accessToken ||
      session?.apiToken ||
      session?.user?.apiToken ||
      undefined
    ) as string | undefined;
  } catch { return undefined; }
}

// Build URLs via explicit base from env to avoid malformed absolute hosts.

/**
 * Returns headers with Authorization bearer token if available.
 * Use with bare fetch() calls so the API can identify authenticated users.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  try {
    const token = await getApiTokenAsync();
    if (token) return { Authorization: `Bearer ${token}` };
  } catch {}
  return {};
}

// Lightweight JSON helpers (now attach auth when available)
export async function postJson<T = any>(path: string, body: any): Promise<T> {
  const url = buildClientUrl(path);
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const token = await getApiTokenAsync();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch { /* proceed without auth */ }
  const res = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    cache: "no-store"
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      // Support ProblemDetails { title, detail }
      msg = j?.message || j?.detail || j?.title || msg;
    } catch {}
    throw new Error(msg);
  }
  try { return await res.json() as T; } catch { return {} as T; }
}

export async function getJson<T = any>(path: string): Promise<T> {
  const url = buildClientUrl(path);
  const headers: Record<string, string> = {};
  try {
    const token = await getApiTokenAsync();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch { /* proceed without auth */ }
  const res = await fetch(url, { cache: "no-store", headers });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); msg = j?.message || msg; } catch {}
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}
// clearApiToken imported above

// Safe JSON parsing that handles empty/invalid responses
// Use this explicitly where you expect potentially empty responses
export async function safeJsonParse(response: Response): Promise<any> {
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (err) {
    throw new Error(`Failed to parse JSON: ${err instanceof Error ? err.message : String(err)}`);
  }
}

// Overloaded apiFetch: supports legacy (path, session, init?) and new (path, init?) signatures
export async function apiFetch(path: string, sessionOrInit?: any, maybeInit?: RequestInit): Promise<Response> {
  let session: any | undefined;
  let init: RequestInit = {};
  if (sessionOrInit && (typeof sessionOrInit === 'object') && ('user' in sessionOrInit || 'apiToken' in sessionOrInit)) {
    session = sessionOrInit;
    init = maybeInit || {};
  } else {
    session = undefined;
    init = (sessionOrInit as RequestInit) || {};
  }
  const token = extractTokenFromSession(session) || await getApiTokenAsync();
  const headers = new Headers(init.headers as any);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !headers.has("Content-Type") && !isFormData) headers.set("Content-Type", "application/json");

  const url = buildClientUrl(path);
  const maxAttempts = 3;
  let attempt = 0;
  let lastErr: any = null;

  // No strict base validation: relative '/api' is allowed and preferred for browser.

  while (attempt < maxAttempts) {
    try {
      const res = await fetch(url, { ...init, headers, credentials: 'include' });
      // 401: clear cached token; optionally redirect when not on auth routes
      if (res.status === 401) {
        try { clearApiToken(); } catch {}
        try {
          if (typeof window !== 'undefined') {
            const cur = window.location.pathname;
            const onAuthRoute = /^(\/login|\/register|\/verify|\/onboarding)/i.test(cur);
            const onPrivateRoute = /^(\/profile|\/chat|\/admin)/i.test(cur);
            if (onAuthRoute) {
              return res; // allow auth pages to handle 401s quietly
            }
            if (onPrivateRoute) {
              const rt = encodeURIComponent(`${cur}${window.location.search}`);
              window.location.assign(`/login?returnTo=${rt}`);
              return res;
            }
            // Public route: do not auto-redirect; let the caller/UI choose how to handle 401s
            return res;
          }
        } catch { /* ignore */ }
        return res;
      }
      if (res.status === 403) {
        // Keep 403 as a normal response; caller may read status/body
      }
      // Retry on transient errors
      if ((res.status >= 500 && res.status <= 599) || res.status === 429) {
        attempt++;
        if (attempt >= maxAttempts) return res;
        const backoff = 200 * Math.pow(2, attempt - 1);
        await new Promise((r) => setTimeout(r, backoff));
        continue;
      }
      return res;
    } catch (e: any) {
      // Wrap network failures (TypeError) with typed code
      lastErr = e;
      if (e?.code === 'unauthorized') throw e;
      if (e instanceof TypeError || /Failed to fetch/i.test(String(e?.message))) {
        const ne: any = new Error('Network request failed');
        ne.code = 'network_failed';
        ne.detail = e?.message || 'Failed to fetch';
        try {
          if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
            window.dispatchEvent(new CustomEvent('api:network_failed', { detail: { url, error: ne } }));
          }
        } catch {}
        throw ne;
      }
      attempt++;
      if (attempt >= maxAttempts) throw e;
      const backoff = 200 * Math.pow(2, attempt - 1);
      await new Promise((r) => setTimeout(r, backoff));
    }
  }
  throw lastErr || new Error("Network error");
}

// NOTE: `apiFetchServer` used to live here but was unreachable from server code
// because this file is `"use client"`. Next.js throws at runtime when a route
// handler imports a client export, which produced cascading 502s on every
// proxied endpoint. Server callers must import from `@/lib/apiServer` instead.

export async function apiGet<T = unknown>(path: string, session: any): Promise<T> {
  const res = await apiFetch(path, session, { method: "GET", cache: 'no-store' });
  if (!res.ok) {
    const message = `${path} ${res.status}`;
    const err: any = new Error(message);
    err.status = res.status;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const body = await res.json();
        err.problem = body;
        if (body?.detail || body?.message) err.message = body.detail || body.message;
      } else {
        const text = await res.text();
        if (text) err.message = text;
      }
    } catch {}
    throw err;
  }
  // Handle 204 No Content - return null instead of trying to parse empty body
  if (res.status === 204) {
    return null as T;
  }
  return (await res.json()) as T;
}

export async function apiPost<T = unknown>(path: string, session: any, body?: unknown): Promise<T> {
  const res = await apiFetch(path, session, { method: "POST", body: body ? (body instanceof FormData ? (body as any) : JSON.stringify(body)) : undefined });
  if (!res.ok) {
    const message = `${path} ${res.status}`;
    const err: any = new Error(message);
    err.status = res.status;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const b = await res.json();
        err.problem = b;
        if (b?.detail || b?.message) err.message = b.detail || b.message;
      } else {
        const t = await res.text();
        if (t) err.message = t;
      }
    } catch {}
    throw err;
  }
  return res.status === 204 ? (undefined as any) : ((await res.json()) as T);
}

export async function apiPut<T = unknown>(path: string, session: any, body?: unknown): Promise<T> {
  const res = await apiFetch(path, session, { method: "PUT", body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) {
    const message = `${path} ${res.status}`;
    const err: any = new Error(message);
    err.status = res.status;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const b = await res.json();
        err.problem = b;
        if (b?.detail || b?.message) err.message = b.detail || b.message;
      } else {
        const t = await res.text();
        if (t) err.message = t;
      }
    } catch {}
    throw err;
  }
  return res.status === 204 ? (undefined as any) : ((await res.json()) as T);
}

export async function apiDelete<T = unknown>(path: string, session: any): Promise<T> {
  const res = await apiFetch(path, session, { method: "DELETE" });
  if (!res.ok) {
    const message = `${path} ${res.status}`;
    const err: any = new Error(message);
    err.status = res.status;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const b = await res.json();
        err.problem = b;
        if (b?.detail || b?.message) err.message = b.detail || b.message;
      } else {
        const t = await res.text();
        if (t) err.message = t;
      }
    } catch {}
    throw err;
  }
  return res.status === 204 ? (undefined as any) : ((await res.json()) as T);
}
